import React, { useState, useEffect } from "react";
import { X, Calendar, Users, Lock, Globe } from "lucide-react";

/**
 * 캘린더 생성/수정 모달
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {function} onClose - 닫기 핸들러
 * @param {function} onSave - 저장 핸들러
 * @param {object} calendar - 수정할 캘린더 정보 (optional)
 */
export default function CalendarModal({
  isOpen,
  onClose,
  onSave,
  calendar = null,
}) {
  const [formData, setFormData] = useState({
    calendar_name: "",
    calendar_type: "personal",
    is_public: false,
    permissions: [],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (calendar) {
      setFormData({
        calendar_name: calendar.calendar_name || "",
        calendar_type: calendar.calendar_type || "personal",
        is_public: calendar.is_public || false,
        permissions: [],
      });
    } else {
      setFormData({
        calendar_name: "",
        calendar_type: "personal",
        is_public: false,
        permissions: [],
      });
    }
    setErrors({});
  }, [calendar, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // 유효성 검사
    const newErrors = {};
    if (!formData.calendar_name.trim()) {
      newErrors.calendar_name = "캘린더 이름을 입력해주세요.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Calendar className="w-6 h-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold">
                {calendar ? "캘린더 수정" : "새 캘린더 만들기"}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* 캘린더 이름 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                캘린더 이름
              </label>
              <input
                type="text"
                value={formData.calendar_name}
                onChange={(e) =>
                  setFormData({ ...formData, calendar_name: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.calendar_name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="캘린더 이름을 입력하세요"
              />
              {errors.calendar_name && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.calendar_name}
                </p>
              )}
            </div>

            {/* 캘린더 유형 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                캘린더 유형
              </label>
              <div className="space-y-2">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="calendar_type"
                    value="personal"
                    checked={formData.calendar_type === "personal"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        calendar_type: e.target.value,
                      })
                    }
                    className="mr-3"
                  />
                  <Lock className="w-4 h-4 text-gray-500 mr-2" />
                  <div>
                    <div className="font-medium">개인 캘린더</div>
                    <div className="text-sm text-gray-500">
                      나만 볼 수 있는 개인 캘린더입니다
                    </div>
                  </div>
                </label>

                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="calendar_type"
                    value="shared"
                    checked={formData.calendar_type === "shared"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        calendar_type: e.target.value,
                      })
                    }
                    className="mr-3"
                  />
                  <Users className="w-4 h-4 text-gray-500 mr-2" />
                  <div>
                    <div className="font-medium">공유 캘린더</div>
                    <div className="text-sm text-gray-500">
                      팀원들과 함께 사용하는 캘린더입니다
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* 공개 여부 (공유 캘린더일 때만) */}
            {formData.calendar_type === "shared" && (
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) =>
                      setFormData({ ...formData, is_public: e.target.checked })
                    }
                    className="mr-3"
                  />
                  <Globe className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm">
                    모든 팀원이 볼 수 있는 공개 캘린더로 설정
                  </span>
                </label>
              </div>
            )}

            {/* 버튼 */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                {calendar ? "수정" : "생성"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
